import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { getUserFrequencyChallengeMetrics, getMarianaTekReservations, getMarianaTekClassSession, getMarianaTekCreditTransaction, getFYSUserProgress, formatFYSProgress, lookupFYSPrize } from '../services/api'
import ChallengeHelpTicketModal from './ChallengeHelpTicketModal'

const Container = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin: 0;
`

const HelpButton = styled.button`
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 4px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    background: #b8002d;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`

const ColumnsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`

const ColumnSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const ColumnTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${theme.color.border.light};
`

const ChallengeCard = styled.div`
  background: ${theme.color.background.frame};
  border: 2px solid ${theme.color.accent.primary};
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 8px ${theme.color.shadow.light};
`

const ChallengeTitle = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.accent.primary};
  margin-bottom: 1.5rem;
  text-align: center;
`

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const ProgressLabel = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  font-weight: 600;
  text-align: center;
`

const ProgressValue = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: ${theme.color.accent.primary};
  text-align: center;
`

const TierBadge = styled.div`
  background: ${theme.color.accent.primary};
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 20px;
  font-size: 1.4rem;
  font-weight: 600;
  text-align: center;
  margin-top: 1rem;
`

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${theme.color.text.grey};
  font-size: 1.6rem;
  font-style: italic;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.color.border.light};
  border-top: 4px solid ${theme.color.accent.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 3rem auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const ErrorMessage = styled.div`
  background: #fee;
  border: 1px solid #fcc;
  color: #c33;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`

const CompletedBadge = styled.div`
  background: #4caf50;
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.6rem;
  font-weight: 700;
  text-align: center;
  margin: 1rem 0;
`

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 12px;
  background-color: ${theme.color.border.light};
  border-radius: 6px;
  overflow: hidden;
  margin: 1rem 0;
`

const ProgressBarInner = styled.div<{ $percentage: number }>`
  width: ${props => props.$percentage || 0}%;
  height: 100%;
  background: linear-gradient(90deg, ${theme.color.accent.primary} 0%, #ff6b4a 100%);
  border-radius: 6px;
  transition: width 0.5s ease-out;
`

const ProgressFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  margin-top: 0.5rem;
`

const PrizeSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${theme.color.border.light};
`

const PrizeTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
`

const PrizeDetail = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: 1.3rem;

  & > span:first-child {
    color: ${theme.color.text.grey};
    font-weight: 600;
  }

  & > span:last-child {
    color: ${theme.color.text.base};
    font-weight: 500;
  }
`

const PrizeImage = styled.img`
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  margin: 1rem auto;
  display: block;
`

const UpgradeBadge = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  margin-top: 0.5rem;
`

function getPrizeImageUrl(prize: any): string | null {
  if (!prize) return null

  if (prize.prize_choice === 'Premium Hoodie' && prize.hoodie_choice) {
    const isChoice1 = prize.hoodie_choice.includes('1')
    const color = (prize.hoodie_color || 'black').toLowerCase()

    if (color.includes('white')) {
      return isChoice1 ? '/images/prizes/hoodie-white-choice1.png' : '/images/prizes/hoodie-white-choice2.png'
    } else if (color.includes('grey') || color.includes('gray')) {
      return isChoice1 ? '/images/prizes/hoodie-grey-choice1.png' : '/images/prizes/hoodie-grey-choice2.png'
    } else {
      // Default to black
      return isChoice1 ? '/images/prizes/hoodie-black-choice1.jpg' : '/images/prizes/hoodie-black-choice2.jpg'
    }
  }

  if (prize.prize_choice === 'Premium Water Bottle' && prize.water_bottle_choice) {
    const isChoice1 = prize.water_bottle_choice.includes('1')
    return isChoice1 ? '/images/prizes/bottle-choice1.jpg' : '/images/prizes/bottle-choice2.jpg'
  }

  return null
}

interface ChallengeDataProps {
  userData: any
  reservationsData?: any
}

const ChallengeData: React.FC<ChallengeDataProps> = ({ userData, reservationsData }) => {
  const [challengeMetrics, setChallengeMetrics] = useState<any>(null)
  const [actualClassCount, setActualClassCount] = useState<number | null>(null)
  const [fysData, setFysData] = useState<any>(null)
  const [fysPrize, setFysPrize] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const [showHelpTicketModal, setShowHelpTicketModal] = useState(false)

  useEffect(() => {
    const fetchChallengeData = async () => {
      if (!userData?.user?.external_id) {
        setError('No Mariana Tek ID found for this user')
        return
      }

      // Demo mode: For j.kliman@barrys.com, show demo challenge data
      if (userData?.user?.email === 'j.kliman@barrys.com') {
        console.log('🎬 Challenge Demo Mode: Generating demo challenge for j.kliman@barrys.com')
        setChallengeMetrics({
          tier: '50',
          tierNumber: 50,
          label: '42 / 50',
          optInDate: '2024-10-01T00:00:00Z'
        })
        setActualClassCount(42)
        // Demo FYS data
        setFysData({
          challengeName: 'Find Your Strength 2026',
          sprintName: '20 Class Track',
          sprintGoal: 20,
          classesCompleted: 8,
          progressPercentage: 40,
          isCompleted: false,
          classesRemaining: 12
        })
        setFysPrize(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError('')

      // Fetch Red Room Records data
      try {
        const metrics = await getUserFrequencyChallengeMetrics(userData.user.external_id)
        setChallengeMetrics(metrics)

        console.log('Challenge metrics:', metrics)
        console.log('Reservations data:', reservationsData)

        if (reservationsData && metrics?.optInDate) {
          console.log('Calculating class count from opt-in date:', metrics.optInDate)
          const count = await calculateActualClassCount(reservationsData, metrics.optInDate)
          console.log('Calculated class count:', count)
          setActualClassCount(count)
        } else {
          setActualClassCount(null)
        }
      } catch (err) {
        console.log('Red Room Records data not available:', err)
        setChallengeMetrics(null)
      }

      // Fetch Find Your Strength 2026 data
      try {
        const fysProgress = await getFYSUserProgress(userData.user.external_id)
        console.log('FYS progress:', fysProgress)
        const formattedFys = formatFYSProgress(fysProgress)
        console.log('Formatted FYS:', formattedFys)
        setFysData(formattedFys)
      } catch (fysErr) {
        console.log('User not enrolled in FYS challenge:', fysErr)
        setFysData(null)
      }

      // Fetch FYS prize selection from Athena (independent of other fetches)
      if (userData?.user?.email) {
        try {
          const prizeResult = await lookupFYSPrize(userData.user.email)
          console.log('FYS prize result:', prizeResult)
          if (prizeResult.found) {
            setFysPrize(prizeResult.prizeSelection)
          } else {
            setFysPrize(null)
          }
        } catch (prizeErr) {
          console.log('FYS prize lookup failed:', prizeErr)
          setFysPrize(null)
        }
      }

      setIsLoading(false)
    }

    fetchChallengeData()
  }, [userData?.user?.external_id, reservationsData])

  const calculateActualClassCount = async (reservationsData: any, optInDate: string): Promise<number> => {
    try {
      const reservationsList = reservationsData.data || []
      console.log('Total reservations to process:', reservationsList.length)

      const optInDateTime = new Date(optInDate).getTime()
      console.log('Opt-in date:', optInDate, 'timestamp:', optInDateTime)

      // Challenge ends November 1st, 2025
      const challengeEndDate = new Date('2025-11-01T23:59:59')
      const challengeEndDateTime = challengeEndDate.getTime()
      console.log('Challenge end date:', challengeEndDate, 'timestamp:', challengeEndDateTime)

      // Filter for checked-in classes within challenge period (opt-in date to November 1st)
      const validReservations = reservationsList.filter((reservation: any) => {
        const status = reservation.attributes?.status?.toLowerCase() || ''

        // Must be checked in
        if (status !== 'check in') {
          return false
        }

        // Must be within challenge period (after opt-in date, before November 1st)
        const checkInDate = reservation.attributes?.check_in_date
        if (checkInDate) {
          const checkInDateTime = new Date(checkInDate).getTime()
          if (checkInDateTime < optInDateTime || checkInDateTime > challengeEndDateTime) {
            return false
          }
        } else {
          return false
        }

        return true
      })

      console.log('Valid checked-in reservations in challenge period:', validReservations.length)

      // Process in batches to avoid overwhelming the API
      const BATCH_SIZE = 10
      const validClasses: boolean[] = []

      for (let i = 0; i < validReservations.length; i += BATCH_SIZE) {
        const batch = validReservations.slice(i, i + BATCH_SIZE)

        const batchResults = await Promise.all(
          batch.map(async (reservation: any, index: any) => {
            const creditTransactionId = reservation.relationships?.credit_transactions?.data?.[0]?.id

            console.log(`Processing reservation ${i + index + 1}:`, {
              id: reservation.id,
              creditTransactionId,
              status: reservation.attributes?.status,
              checkInDate: reservation.attributes?.check_in_date
            })

            if (!creditTransactionId) {
              console.log(`  → No credit transaction ID, including in count`)
              return true // Include if no credit transaction
            }

            try {
              const creditTransactionData = await getMarianaTekCreditTransaction(creditTransactionId)
              const creditName = creditTransactionData?.data?.attributes?.credit_name || ''

              console.log(`  → Credit name: "${creditName}"`)

              // Exclude free/comp classes
              const excludePatterns = [/free/i, /comp/i, /complimentary/i]
              const isExcluded = excludePatterns.some(pattern => pattern.test(creditName))

              console.log(`  → Is excluded (free/comp): ${isExcluded}`)

              return !isExcluded
            } catch (err) {
              console.error('  → Failed to fetch credit transaction:', err)
              return true // Include if we can't determine
            }
          })
        )

        validClasses.push(...batchResults)
      }

      // Count only the valid classes (where the promise resolved to true)
      const finalCount = validClasses.filter(Boolean).length
      console.log('Final class count after filtering free/comp:', finalCount)
      return finalCount
    } catch (err) {
      console.error('Failed to calculate actual class count:', err)
      return 0
    }
  }

  if (isLoading) {
    return (
      <Container>
        <TitleContainer>
          <Title>Challenge Data</Title>
          <HelpButton onClick={() => setShowHelpTicketModal(true)}>
            Challenge Help Ticket
          </HelpButton>
        </TitleContainer>
        <LoadingSpinner />
        <ChallengeHelpTicketModal
          isOpen={showHelpTicketModal}
          onClose={() => setShowHelpTicketModal(false)}
          userData={userData}
        />
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <TitleContainer>
          <Title>Challenge Data</Title>
          <HelpButton onClick={() => setShowHelpTicketModal(true)}>
            Challenge Help Ticket
          </HelpButton>
        </TitleContainer>
        <ErrorMessage>{error}</ErrorMessage>
        <ChallengeHelpTicketModal
          isOpen={showHelpTicketModal}
          onClose={() => setShowHelpTicketModal(false)}
          userData={userData}
        />
      </Container>
    )
  }

  if (!challengeMetrics) {
    return (
      <Container>
        <TitleContainer>
          <Title>Challenge Data</Title>
          <HelpButton onClick={() => setShowHelpTicketModal(true)}>
            Challenge Help Ticket
          </HelpButton>
        </TitleContainer>
        <ColumnsContainer>
          <ColumnSection>
            <ColumnTitle>Previous Campaigns</ColumnTitle>
            <NoDataMessage>
              User is not enrolled in Red Room Records
            </NoDataMessage>
          </ColumnSection>
          <ColumnSection>
            <ColumnTitle>Current Campaigns</ColumnTitle>
            {fysData ? (
              <ChallengeCard>
                <ChallengeTitle>💪 {fysData.challengeName}</ChallengeTitle>
                <ProgressContainer>
                  <ProgressLabel>Progress</ProgressLabel>
                  <ProgressValue>
                    {fysData.classesCompleted} / {fysData.sprintGoal}
                  </ProgressValue>
                </ProgressContainer>
                {fysData.isCompleted ? (
                  <CompletedBadge>Challenge Complete!</CompletedBadge>
                ) : (
                  <>
                    <ProgressBarOuter>
                      <ProgressBarInner $percentage={fysData.progressPercentage} />
                    </ProgressBarOuter>
                    <ProgressFooter>
                      <span>{fysData.progressPercentage}%</span>
                      <span>{fysData.classesRemaining} classes to go</span>
                    </ProgressFooter>
                  </>
                )}
                <TierBadge>
                  {fysData.sprintName}
                </TierBadge>
                {(fysData.isCompleted || fysPrize) && (
                  <PrizeSection>
                    <PrizeTitle>Prize Selection</PrizeTitle>
                    {fysPrize ? (
                      <>
                        <PrizeDetail>
                          <span>Prize:</span>
                          <span>{fysPrize.prize_choice}</span>
                        </PrizeDetail>
                        {fysPrize.prize_choice === 'Premium Hoodie' && (
                          <>
                            {fysPrize.hoodie_choice && (
                              <PrizeDetail>
                                <span>Style:</span>
                                <span>{fysPrize.hoodie_choice}</span>
                              </PrizeDetail>
                            )}
                            {fysPrize.hoodie_color && (
                              <PrizeDetail>
                                <span>Color:</span>
                                <span>{fysPrize.hoodie_color}</span>
                              </PrizeDetail>
                            )}
                            {fysPrize.sizing && (
                              <PrizeDetail>
                                <span>Size:</span>
                                <span>{fysPrize.sizing}</span>
                              </PrizeDetail>
                            )}
                          </>
                        )}
                        {fysPrize.prize_choice === 'Premium Water Bottle' && fysPrize.water_bottle_choice && (
                          <PrizeDetail>
                            <span>Style:</span>
                            <span>{fysPrize.water_bottle_choice}</span>
                          </PrizeDetail>
                        )}
                        {getPrizeImageUrl(fysPrize) && (
                          <PrizeImage src={getPrizeImageUrl(fysPrize)!} alt={fysPrize.prize_choice} />
                        )}
                        <PrizeDetail>
                          <span>Track:</span>
                          <span>{fysPrize.challenge_track}</span>
                        </PrizeDetail>
                        {fysPrize.prize_choice === 'Upgrade to 20 Class Track' && (
                          <UpgradeBadge>Upgraded from 12 to 20 Class Track</UpgradeBadge>
                        )}
                        {fysPrize.submitted_at && (
                          <PrizeDetail>
                            <span>Completed:</span>
                            <span>{new Date(fysPrize.submitted_at).toLocaleDateString()}</span>
                          </PrizeDetail>
                        )}
                      </>
                    ) : (
                      <NoDataMessage style={{ padding: '1rem', fontSize: '1.3rem' }}>No prize selected yet</NoDataMessage>
                    )}
                  </PrizeSection>
                )}
              </ChallengeCard>
            ) : fysPrize ? (
              <ChallengeCard>
                <ChallengeTitle>💪 Find Your Strength 2026</ChallengeTitle>
                <NoDataMessage style={{ padding: '1rem', fontSize: '1.3rem' }}>Not enrolled in FYS tracking</NoDataMessage>
                <PrizeSection>
                  <PrizeTitle>Prize Selection</PrizeTitle>
                  <PrizeDetail>
                    <span>Prize:</span>
                    <span>{fysPrize.prize_choice}</span>
                  </PrizeDetail>
                  {fysPrize.prize_choice === 'Premium Hoodie' && (
                    <>
                      {fysPrize.hoodie_choice && (
                        <PrizeDetail>
                          <span>Style:</span>
                          <span>{fysPrize.hoodie_choice}</span>
                        </PrizeDetail>
                      )}
                      {fysPrize.hoodie_color && (
                        <PrizeDetail>
                          <span>Color:</span>
                          <span>{fysPrize.hoodie_color}</span>
                        </PrizeDetail>
                      )}
                      {fysPrize.sizing && (
                        <PrizeDetail>
                          <span>Size:</span>
                          <span>{fysPrize.sizing}</span>
                        </PrizeDetail>
                      )}
                    </>
                  )}
                  {fysPrize.prize_choice === 'Premium Water Bottle' && fysPrize.water_bottle_choice && (
                    <PrizeDetail>
                      <span>Style:</span>
                      <span>{fysPrize.water_bottle_choice}</span>
                    </PrizeDetail>
                  )}
                  {getPrizeImageUrl(fysPrize) && (
                    <PrizeImage src={getPrizeImageUrl(fysPrize)!} alt={fysPrize.prize_choice} />
                  )}
                  <PrizeDetail>
                    <span>Track:</span>
                    <span>{fysPrize.challenge_track}</span>
                  </PrizeDetail>
                  {fysPrize.prize_choice === 'Upgrade to 20 Class Track' && (
                    <UpgradeBadge>Upgraded from 12 to 20 Class Track</UpgradeBadge>
                  )}
                  {fysPrize.submitted_at && (
                    <PrizeDetail>
                      <span>Completed:</span>
                      <span>{new Date(fysPrize.submitted_at).toLocaleDateString()}</span>
                    </PrizeDetail>
                  )}
                </PrizeSection>
              </ChallengeCard>
            ) : (
              <NoDataMessage>
                User is not enrolled in Find Your Strength 2026
              </NoDataMessage>
            )}
          </ColumnSection>
        </ColumnsContainer>
        <ChallengeHelpTicketModal
          isOpen={showHelpTicketModal}
          onClose={() => setShowHelpTicketModal(false)}
          userData={userData}
        />
      </Container>
    )
  }

  console.log('Rendering ChallengeData - actualClassCount:', actualClassCount, 'challengeMetrics:', challengeMetrics)

  return (
    <Container>
      <TitleContainer>
        <Title>Challenge Data</Title>
        <HelpButton onClick={() => setShowHelpTicketModal(true)}>
          Challenge Help Ticket
        </HelpButton>
      </TitleContainer>
      <ColumnsContainer>
        <ColumnSection>
          <ColumnTitle>Previous Campaigns</ColumnTitle>
          <ChallengeCard>
            <ChallengeTitle>💿 Red Room Records</ChallengeTitle>
            <ProgressContainer>
              <ProgressLabel>Progress</ProgressLabel>
              <ProgressValue>
                {actualClassCount !== null ? `${actualClassCount} / ${challengeMetrics.tierNumber}` : challengeMetrics.label}
              </ProgressValue>
            </ProgressContainer>
            <TierBadge>
              {challengeMetrics.tier} Class Challenge
            </TierBadge>
          </ChallengeCard>
        </ColumnSection>

        <ColumnSection>
          <ColumnTitle>Current Campaigns</ColumnTitle>
          {fysData ? (
            <ChallengeCard>
              <ChallengeTitle>💪 {fysData.challengeName}</ChallengeTitle>
              <ProgressContainer>
                <ProgressLabel>Progress</ProgressLabel>
                <ProgressValue>
                  {fysData.classesCompleted} / {fysData.sprintGoal}
                </ProgressValue>
              </ProgressContainer>
              {fysData.isCompleted ? (
                <CompletedBadge>Challenge Complete!</CompletedBadge>
              ) : (
                <>
                  <ProgressBarOuter>
                    <ProgressBarInner $percentage={fysData.progressPercentage} />
                  </ProgressBarOuter>
                  <ProgressFooter>
                    <span>{fysData.progressPercentage}%</span>
                    <span>{fysData.classesRemaining} classes to go</span>
                  </ProgressFooter>
                </>
              )}
              <TierBadge>
                {fysData.sprintName}
              </TierBadge>
              {(fysData.isCompleted || fysPrize) && (
                <PrizeSection>
                  <PrizeTitle>Prize Selection</PrizeTitle>
                  {fysPrize ? (
                    <>
                      <PrizeDetail>
                        <span>Prize:</span>
                        <span>{fysPrize.prize_choice}</span>
                      </PrizeDetail>
                      {fysPrize.prize_choice === 'Premium Hoodie' && (
                        <>
                          {fysPrize.hoodie_choice && (
                            <PrizeDetail>
                              <span>Style:</span>
                              <span>{fysPrize.hoodie_choice}</span>
                            </PrizeDetail>
                          )}
                          {fysPrize.hoodie_color && (
                            <PrizeDetail>
                              <span>Color:</span>
                              <span>{fysPrize.hoodie_color}</span>
                            </PrizeDetail>
                          )}
                          {fysPrize.sizing && (
                            <PrizeDetail>
                              <span>Size:</span>
                              <span>{fysPrize.sizing}</span>
                            </PrizeDetail>
                          )}
                        </>
                      )}
                      {fysPrize.prize_choice === 'Premium Water Bottle' && fysPrize.water_bottle_choice && (
                        <PrizeDetail>
                          <span>Style:</span>
                          <span>{fysPrize.water_bottle_choice}</span>
                        </PrizeDetail>
                      )}
                      {getPrizeImageUrl(fysPrize) && (
                        <PrizeImage src={getPrizeImageUrl(fysPrize)!} alt={fysPrize.prize_choice} />
                      )}
                      <PrizeDetail>
                        <span>Track:</span>
                        <span>{fysPrize.challenge_track}</span>
                      </PrizeDetail>
                      {fysPrize.prize_choice === 'Upgrade to 20 Class Track' && (
                        <UpgradeBadge>Upgraded from 12 to 20 Class Track</UpgradeBadge>
                      )}
                      {fysPrize.submitted_at && (
                        <PrizeDetail>
                          <span>Completed:</span>
                          <span>{new Date(fysPrize.submitted_at).toLocaleDateString()}</span>
                        </PrizeDetail>
                      )}
                    </>
                  ) : (
                    <NoDataMessage style={{ padding: '1rem', fontSize: '1.3rem' }}>No prize selected yet</NoDataMessage>
                  )}
                </PrizeSection>
              )}
            </ChallengeCard>
          ) : (
            <NoDataMessage>
              User is not enrolled in Find Your Strength 2026
            </NoDataMessage>
          )}
        </ColumnSection>
      </ColumnsContainer>
      <ChallengeHelpTicketModal
        isOpen={showHelpTicketModal}
        onClose={() => setShowHelpTicketModal(false)}
        userData={userData}
      />
    </Container>
  )
}

export default ChallengeData
